import type { CSSProperties } from 'react';

import type { WebsiteConfiguration } from '@cineamo/api/models/website-configuration/WebsiteConfiguration.types';
import type { WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { DefaultWebsiteStyle } from '../constants/website-configuration-constants';

import { isThemeLight, isValidHexColor } from './color-helper';

/**
 * How a cinema's website looks, resolved once for every place that draws it: cinema-website's
 * `getDomainConfiguration` and the pages editor's canvas. One function on purpose — two copies of the
 * merge are two palettes the day one of them changes.
 */

/** A cinema has a dark theme only when it configured a `darkStyle`; otherwise the site is always light. */
export function hasDarkStyle(websiteConfiguration: WebsiteConfiguration | undefined): boolean {
    return Object.keys(websiteConfiguration?.darkStyle ?? {}).length > 0;
}

/**
 * The effective palette: the defaults, the cinema's own style over them, and its dark overrides on top
 * when `colorTheme` is dark. The base is `DefaultWebsiteStyle`, not `cineamoWebsiteColors`.
 */
export function resolveWebsiteStyle(
    websiteConfiguration: WebsiteConfiguration | undefined,
    colorTheme: 'light' | 'dark'
): WebsiteStyle {
    return {
        ...DefaultWebsiteStyle,
        ...websiteConfiguration?.style,
        ...(colorTheme === 'dark' ? websiteConfiguration?.darkStyle : {})
    } as WebsiteStyle;
}

/**
 * The color scheme the *browser* should use for everything it paints itself rather than us: the
 * canvas behind the document, the overscroll area, native controls and scrollbars — and, on iOS 26,
 * the translucent material of the top bar. Safari derives that material's light/dark appearance
 * from the root element's color scheme, and with nothing declared it assumes light, which is why
 * a dark cinema got a white bar.
 *
 * Derived from the palette, not from `data-color-theme`: that attribute says which of the two
 * configured styles is active, not whether it is dark. A cinema without a `darkStyle` is always
 * `light` there, even with a background of `#042968`. The comparison against `onBackgroundColor`
 * reuses the existing theme detection instead of inventing a luminance threshold.
 *
 * A single keyword on purpose. `light dark` would defer to the visitor's device and hand a
 * dark-configured cinema a white canvas in light mode; `only light` additionally keeps Chrome's
 * auto dark theme from inverting a light cinema.
 */
export function resolveUaColorScheme(style: WebsiteStyle | undefined): 'dark' | 'only light' {
    const background = style?.backgroundColor;
    const onBackground = style?.onBackgroundColor;
    if (!background || !isValidHexColor(background) || !onBackground || !isValidHexColor(onBackground)) {
        return 'only light';
    }
    return isThemeLight(background, onBackground) ? 'only light' : 'dark';
}

/**
 * The color layer of the page background: the configured background color, or the gradient
 * between it and `backgroundColorGradient`.
 *
 * The value is only meaningful on a viewport-sized element — that is how the website's layouts paint
 * it (see `PageBackgroundColorLayer`), so the gradient maps onto the viewport rather than onto the
 * element. Anything reproducing the page background has to keep that geometry to get the same
 * pixels.
 *
 * Always a `background-image`, never a `background-color`, even for a single color. Safari 26
 * ignores `<meta name="theme-color">` and instead derives its top bar from the `position: fixed`
 * and `sticky` boxes at the viewport edges: given a visible `background-color` on one, it replaces
 * the translucent material with an opaque block of that color, hides the scroll-edge fade, and
 * keeps that color for the rest of the page visit. Declaring none keeps the bar translucent for
 * this layer, verified on iOS 26.
 *
 * Do not read that as "background images are invisible to Safari" — they are not. Where no
 * `background-color` is found, WebKit falls back to sampling the pixels of the fixed and sticky
 * layers, and a small sticky banner painting a flat color that way does still tint the bar (which
 * is why the app-download banner turns it black). A viewport-sized layer like this one is the case
 * that stays translucent. The color the browser UI should tint itself with comes from the document
 * canvas instead — see the `html, body` rule in cinema-website's `globals.css`, which cannot form
 * such an edge.
 */
export function getPageBackgroundStyle(style: WebsiteStyle | undefined): CSSProperties {
    const from = style?.backgroundColor;
    if (!from) {
        return {};
    }
    const to = style?.backgroundColorGradient || from;
    return {
        backgroundImage: `linear-gradient(var(--gradient-angle, 135deg), ${from}, ${to})`
    };
}
